/**
 * CollegeEventBus - Central Event Coordination System
 *
 * Purpose: Event-driven architecture for all college and milestone changes.
 * Ensures task integrity, crisis rescue awareness, and smart scheduling coordination.
 *
 * Key Integration Points:
 * - CrisisRescueService: Events trigger crisis re-evaluation
 * - WeeklyCapacityService: Round changes trigger capacity recalculation
 * - AcademicCalendarService: Task regeneration respects academic constraints
 * - TaskBatchingService: Events trigger batch reorganization
 *
 * Design Principles:
 * - Idempotent event handlers (safe to retry)
 * - Audit trail for all operations
 * - Graceful degradation on handler failures
 * - Support for event replay (debugging/recovery)
 */

import { supabase } from '../../lib/supabase.js';

class CollegeEventBus {
  // Event type constants
  static events = {
    // College lifecycle
    COLLEGE_ADDED: 'college:added',
    COLLEGE_UPDATED: 'college:updated',
    COLLEGE_REMOVED: 'college:removed',

    // Application round changes (CRITICAL for timeline integrity)
    APPLICATION_ROUND_CHANGED: 'college:round_changed',

    // Deadline changes (affects crisis detection)
    DEADLINE_CHANGED: 'college:deadline_changed',

    // College category changes (affects batching)
    CATEGORY_CHANGED: 'college:category_changed',

    // Template lifecycle
    TEMPLATE_UPDATED: 'template:updated',
    TEMPLATE_ACTIVATED: 'template:activated',
    TEMPLATE_DEACTIVATED: 'template:deactivated',

    // Task lifecycle (for crisis monitoring)
    TASK_GENERATED: 'task:generated',
    TASK_COMPLETED: 'task:completed',
    TASK_DELETED: 'task:deleted',

    // Integrity events
    INTEGRITY_CHECK_TRIGGERED: 'integrity:check_triggered',
    INTEGRITY_ISSUE_DETECTED: 'integrity:issue_detected',
    INTEGRITY_AUTO_HEALED: 'integrity:auto_healed'
  };

  // Handler registry: eventType -> array of handlers
  static handlers = new Map();

  // Event history for audit trail and debugging
  static eventHistory = [];
  static maxHistorySize = 1000;

  // Rate limiting to prevent event storms
  static rateLimits = new Map();
  static maxEventsPerCollegePer5Min = 5;

  // Circuit breaker for auto-healing
  static circuitBreaker = {
    failureCount: 0,
    maxFailures: 10,
    resetAfterMinutes: 15,
    lastResetTime: Date.now(),
    isOpen: false
  };

  /**
   * Emit an event with full context
   * @param {string} eventType - Event type constant
   * @param {Object} payload - Event payload
   * @param {Object} options - Emission options
   * @returns {Promise<Object>} Emission results
   */
  static async emit(eventType, payload, options = {}) {
    const {
      skipRateLimit = false,
      skipAudit = false,
      priority = 'normal', // 'critical' | 'high' | 'normal' | 'low'
      source = 'system' // 'user' | 'system' | 'admin' | 'api'
    } = options;

    console.log(`[CollegeEventBus] 📢 Emitting event: ${eventType}`, {
      payload,
      priority,
      source
    });

    try {
      // Rate limiting (except for critical events)
      if (!skipRateLimit && priority !== 'critical') {
        const rateLimitCheck = this.checkRateLimit(eventType, payload);
        if (rateLimitCheck.exceeded) {
          console.warn(`[CollegeEventBus] ⚠️  Rate limit exceeded for ${eventType}`, rateLimitCheck);
          return {
            success: false,
            rateLimited: true,
            message: 'Event rate limit exceeded. Try again in a few minutes.',
            retryAfter: rateLimitCheck.retryAfter
          };
        }
      }

      // Create event record
      const event = {
        id: this.generateEventId(),
        type: eventType,
        payload,
        timestamp: new Date().toISOString(),
        priority,
        source,
        handlerResults: []
      };

      // Add to history
      this.addToHistory(event);

      // Audit log (unless skipped for performance-critical paths)
      if (!skipAudit) {
        await this.auditLogEvent(event);
      }

      // Get handlers for this event type
      const eventHandlers = this.handlers.get(eventType) || [];

      if (eventHandlers.length === 0) {
        console.warn(`[CollegeEventBus] No handlers registered for ${eventType}`);
        return {
          success: true,
          handlersExecuted: 0,
          event
        };
      }

      // Execute handlers (parallel for non-dependent handlers)
      const handlerPromises = eventHandlers.map(async (handler) => {
        const handlerStart = Date.now();

        try {
          const result = await handler.fn(payload, event);
          const handlerDuration = Date.now() - handlerStart;

          console.log(`[CollegeEventBus] ✅ Handler ${handler.name} completed in ${handlerDuration}ms`);

          return {
            handlerName: handler.name,
            success: true,
            duration: handlerDuration,
            result
          };
        } catch (error) {
          const handlerDuration = Date.now() - handlerStart;

          console.error(`[CollegeEventBus] ❌ Handler ${handler.name} failed:`, error);

          // Increment circuit breaker
          this.incrementCircuitBreaker();

          return {
            handlerName: handler.name,
            success: false,
            duration: handlerDuration,
            error: error.message,
            stack: error.stack
          };
        }
      });

      // Wait for all handlers
      const handlerResults = await Promise.all(handlerPromises);
      event.handlerResults = handlerResults;

      // Check for failures
      const failedHandlers = handlerResults.filter(r => !r.success);
      const successfulHandlers = handlerResults.filter(r => r.success);

      if (failedHandlers.length > 0) {
        console.error(`[CollegeEventBus] ⚠️  ${failedHandlers.length}/${eventHandlers.length} handlers failed for ${eventType}`);
      }

      return {
        success: successfulHandlers.length > 0, // Success if at least one handler succeeded
        handlersExecuted: eventHandlers.length,
        successfulHandlers: successfulHandlers.length,
        failedHandlers: failedHandlers.length,
        handlerResults,
        event
      };

    } catch (error) {
      console.error(`[CollegeEventBus] 💥 Failed to emit event ${eventType}:`, error);

      return {
        success: false,
        error: error.message,
        stack: error.stack
      };
    }
  }

  /**
   * Register a handler for a specific event type
   * @param {string} eventType - Event type to listen for
   * @param {Function} handlerFn - Handler function (payload, event) => Promise<result>
   * @param {Object} options - Handler options
   * @returns {Function} Unsubscribe function
   */
  static on(eventType, handlerFn, options = {}) {
    const {
      name = 'anonymous',
      priority = 'normal',
      filter = null // Optional filter function: (payload) => boolean
    } = options;

    console.log(`[CollegeEventBus] Registering handler "${name}" for ${eventType}`);

    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, []);
    }

    const handler = {
      fn: filter ? this.createFilteredHandler(handlerFn, filter) : handlerFn,
      name,
      priority,
      registeredAt: new Date().toISOString()
    };

    const handlers = this.handlers.get(eventType);
    handlers.push(handler);

    // Sort by priority
    handlers.sort((a, b) => {
      const priorityOrder = { critical: 4, high: 3, normal: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });

    // Return unsubscribe function
    return () => {
      const handlers = this.handlers.get(eventType) || [];
      const index = handlers.findIndex(h => h === handler);
      if (index > -1) {
        handlers.splice(index, 1);
        console.log(`[CollegeEventBus] Unregistered handler "${name}" from ${eventType}`);
      }
    };
  }

  /**
   * Register multiple handlers at once
   * @param {Object} handlerMap - Map of event types to handler functions
   */
  static registerHandlers(handlerMap) {
    const unsubscribeFns = [];

    for (const [eventType, config] of Object.entries(handlerMap)) {
      const { handler, ...options } = config;
      const unsubscribe = this.on(eventType, handler, options);
      unsubscribeFns.push(unsubscribe);
    }

    // Return function that unsubscribes all
    return () => {
      unsubscribeFns.forEach(fn => fn());
    };
  }

  /**
   * Create a filtered handler wrapper
   * @private
   */
  static createFilteredHandler(handlerFn, filter) {
    return async (payload, event) => {
      if (filter(payload)) {
        return await handlerFn(payload, event);
      }
      return { skipped: true, reason: 'Filter did not match' };
    };
  }

  /**
   * Check rate limit for event
   * @private
   */
  static checkRateLimit(eventType, payload) {
    const key = this.getRateLimitKey(eventType, payload);
    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000);

    // Get events in last 5 minutes
    const recentEvents = this.rateLimits.get(key) || [];
    const recentCount = recentEvents.filter(timestamp => timestamp > fiveMinutesAgo).length;

    if (recentCount >= this.maxEventsPerCollegePer5Min) {
      const oldestEventTime = Math.min(...recentEvents);
      const retryAfter = Math.ceil((oldestEventTime + (5 * 60 * 1000) - now) / 1000);

      return {
        exceeded: true,
        recentCount,
        limit: this.maxEventsPerCollegePer5Min,
        retryAfter // seconds
      };
    }

    // Add current event
    recentEvents.push(now);

    // Keep only recent events
    const filtered = recentEvents.filter(timestamp => timestamp > fiveMinutesAgo);
    this.rateLimits.set(key, filtered);

    return {
      exceeded: false,
      recentCount: filtered.length,
      limit: this.maxEventsPerCollegePer5Min
    };
  }

  /**
   * Get rate limit key for event
   * @private
   */
  static getRateLimitKey(eventType, payload) {
    // Rate limit per college for college events
    if (payload.collegeId) {
      return `${eventType}:${payload.collegeId}`;
    }

    // Rate limit per user for user events
    if (payload.userId) {
      return `${eventType}:${payload.userId}`;
    }

    // Global rate limit for other events
    return `${eventType}:global`;
  }

  /**
   * Generate unique event ID
   * @private
   */
  static generateEventId() {
    return `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add event to history
   * @private
   */
  static addToHistory(event) {
    this.eventHistory.unshift(event);

    // Trim history if too large
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory = this.eventHistory.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Audit log event to database
   * @private
   */
  static async auditLogEvent(event) {
    try {
      await supabase.from('task_audit_log').insert({
        operation_type: event.type,
        user_id: event.payload.userId || null,
        college_id: event.payload.collegeId || null,
        template_id: event.payload.templateId || null,
        timeline_item_id: event.payload.timelineItemId || null,
        metadata: {
          event_id: event.id,
          priority: event.priority,
          source: event.source,
          payload: event.payload,
          timestamp: event.timestamp
        }
      });
    } catch (error) {
      console.error('[CollegeEventBus] Failed to audit log event:', error);
      // Don't throw - audit logging should not break event emission
    }
  }

  /**
   * Circuit breaker management
   * @private
   */
  static incrementCircuitBreaker() {
    this.circuitBreaker.failureCount++;

    if (this.circuitBreaker.failureCount >= this.circuitBreaker.maxFailures) {
      this.circuitBreaker.isOpen = true;
      console.error('[CollegeEventBus] 🚨 Circuit breaker OPENED - too many handler failures');

      // Auto-reset after timeout
      setTimeout(() => {
        this.resetCircuitBreaker();
      }, this.circuitBreaker.resetAfterMinutes * 60 * 1000);
    }
  }

  /**
   * Reset circuit breaker
   * @private
   */
  static resetCircuitBreaker() {
    this.circuitBreaker.failureCount = 0;
    this.circuitBreaker.isOpen = false;
    this.circuitBreaker.lastResetTime = Date.now();
    console.log('[CollegeEventBus] Circuit breaker RESET');
  }

  /**
   * Check if circuit breaker is open
   */
  static isCircuitBreakerOpen() {
    return this.circuitBreaker.isOpen;
  }

  /**
   * Get event history (for debugging)
   * @param {Object} filters - Filter options
   * @returns {Array} Filtered event history
   */
  static getEventHistory(filters = {}) {
    let filtered = [...this.eventHistory];

    if (filters.eventType) {
      filtered = filtered.filter(e => e.type === filters.eventType);
    }

    if (filters.userId) {
      filtered = filtered.filter(e => e.payload.userId === filters.userId);
    }

    if (filters.collegeId) {
      filtered = filtered.filter(e => e.payload.collegeId === filters.collegeId);
    }

    if (filters.limit) {
      filtered = filtered.slice(0, filters.limit);
    }

    return filtered;
  }

  /**
   * Clear rate limits (for testing/admin)
   */
  static clearRateLimits() {
    this.rateLimits.clear();
    console.log('[CollegeEventBus] Rate limits cleared');
  }

  /**
   * Clear all handlers (for testing)
   */
  static clearAllHandlers() {
    this.handlers.clear();
    console.log('[CollegeEventBus] All handlers cleared');
  }

  /**
   * Get handler statistics
   */
  static getHandlerStats() {
    const stats = {};

    for (const [eventType, handlers] of this.handlers.entries()) {
      stats[eventType] = {
        handlerCount: handlers.length,
        handlers: handlers.map(h => ({
          name: h.name,
          priority: h.priority,
          registeredAt: h.registeredAt
        }))
      };
    }

    return stats;
  }
}

export default CollegeEventBus;
